'use client'

import { useEffect, useState } from 'react'

/**
 * Script loader state
 */
export interface IChatkitScriptState {
  /** Whether the ChatKit script and web component are fully loaded */
  isChatkitLoaded: boolean
  /** Error message if script loading failed */
  scriptError: string | null
  /** Whether the script is currently loading */
  isLoadingScript: boolean
}

/**
 * Hook to load the ChatKit script dynamically with error handling and retry
 * Loads the script once and caches it across the application
 * @returns Script loading state
 */
export function useChatkitScript(): IChatkitScriptState {
  const [isChatkitLoaded, setIsChatkitLoaded] = useState(false)
  const [scriptError, setScriptError] = useState<string | null>(null)
  const [isLoadingScript, setIsLoadingScript] = useState(true)

  useEffect(() => {
    const SCRIPT_URL =
      'https://cdn.platform.openai.com/deployments/chatkit/chatkit.js'
    const TIMEOUT_MS = 15000 // 15 seconds timeout
    const MAX_RETRIES = 2

    let retryCount = 0
    let timeoutId: NodeJS.Timeout | null = null
    let scriptElement: HTMLScriptElement | null = null

    /**
     * Loads the ChatKit script dynamically with timeout and error handling
     */
    const loadScript = () => {
      return new Promise<void>((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.querySelector(
          `script[src="${SCRIPT_URL}"]`,
        )
        if (existingScript) {
          console.log('✅ ChatKit script already loaded')
          resolve()
          return
        }

        console.log(
          `🔄 Loading ChatKit script (attempt ${retryCount + 1}/${MAX_RETRIES + 1})...`,
        )

        // Create script element
        scriptElement = document.createElement('script')
        scriptElement.src = SCRIPT_URL
        scriptElement.async = true

        // Set timeout
        timeoutId = setTimeout(() => {
          reject(new Error('Script load timeout'))
        }, TIMEOUT_MS)

        // Success handler
        scriptElement.onload = () => {
          if (timeoutId) {
            clearTimeout(timeoutId)
          }
          console.log('✅ ChatKit script loaded successfully')
          resolve()
        }

        // Error handler
        scriptElement.onerror = (error) => {
          if (timeoutId) {
            clearTimeout(timeoutId)
          }
          console.error('❌ ChatKit script failed to load:', error)
          reject(new Error('Script load failed'))
        }

        // Add to document
        document.head.appendChild(scriptElement)
      })
    }

    /**
     * Attempts to load the script with retries
     */
    const loadWithRetry = async () => {
      while (retryCount <= MAX_RETRIES) {
        try {
          await loadScript()
          // Script loaded, now wait for web component
          await checkChatkit()
          return
        } catch (error) {
          retryCount++
          if (retryCount > MAX_RETRIES) {
            const errorMessage =
              error instanceof Error ? error.message : 'Unknown error'
            console.error(
              '❌ Failed to load ChatKit after retries:',
              errorMessage,
            )
            setScriptError(
              `Failed to load ChatKit: ${errorMessage}. Please check your internet connection.`,
            )
            setIsLoadingScript(false)
            return
          }
          // Wait before retry (exponential backoff)
          const waitTime = Math.min(1000 * 2 ** (retryCount - 1), 5000)
          console.log(`⏳ Retrying in ${waitTime}ms...`)
          await new Promise((resolve) => {
            return setTimeout(resolve, waitTime)
          })
        }
      }
    }

    /**
     * Checks if ChatKit web component is registered
     */
    const checkChatkit = async () => {
      return new Promise<void>((resolve, reject) => {
        const CHECK_INTERVAL = 500
        const MAX_WAIT = 10000
        let elapsed = 0

        const interval = setInterval(() => {
          if (customElements.get('openai-chatkit')) {
            clearInterval(interval)
            console.log('✅ ChatKit web component registered and ready')
            setIsChatkitLoaded(true)
            setIsLoadingScript(false)
            resolve()
          } else {
            elapsed += CHECK_INTERVAL
            if (elapsed >= MAX_WAIT) {
              clearInterval(interval)
              reject(new Error('Web component registration timeout'))
            }
          }
        }, CHECK_INTERVAL)
      })
    }

    loadWithRetry()

    // Cleanup
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  return {
    isChatkitLoaded,
    scriptError,
    isLoadingScript,
  }
}
